import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { push } from 'connected-react-router'
import { toast } from 'react-toastify';

// amplify
import { API, Auth, Storage } from 'aws-amplify'
import { listFundraisingProfilesByOrganization, listProgramsByOrganization, listFundraisingCampaignsByOrganization, getFundraisingCampaign, listFundraisingCampaignAmbassadorsByCampaign, listFundraisingCampaignAmbassadorsByFundraisingProfile, listFundraisingCampaignUpdatesByFundraisingCampaign, listLatentOrganizationCodes, listOrganizationCodesByOrganizationID, listOrganizationsByCode } from './../../graphql/queries'
import { createProgram, createFundraisingInvitation, createFundraisingProfile, sendFundraisingInvitationEmail, updateFundraisingCampaign, createFundraisingCampaign, createFundraisingCampaignAmbassador, updateFundraisingCampaignUpdate, createFundraisingCampaignUpdate, deleteFundraisingProfile, createLatentOrganizationCode, updateFundraisingCampaignAmbassador, deleteFundraisingCampaignAmbassador } from './../../graphql/mutations'

import { setLoading } from './loading'
import { reloadAuthAccount } from './authAccount'

import { Program, StaffProfile, FundraisingProfile, FundraisingCampaign, FundraisingCampaignAmbassador, FundraisingCampaignUpdate, FundraisingCampaignAmbassadorStatus, LatentOrganizationCode } from '../../../API'

interface FundraisingState {
    connectedAccountLink: string | null,
    fundraisingOrganization: any,
    campaigns: FundraisingCampaign[] | null,
    selectedCampaign: FundraisingCampaign | null,
    ambassadors: FundraisingProfile[] | null,
    selectedCampaignAmbassadors: FundraisingCampaignAmbassador[] | null
    selectedCampaignUpdates: FundraisingCampaignUpdate[] | null
    selectedCampaignUpdate: FundraisingCampaignUpdate | null,
    latentOrganizationCodes: LatentOrganizationCode[] | null,
    classyCampaigns: any[] | null
}

const initialState: FundraisingState = {
    connectedAccountLink: null,
    fundraisingOrganization: null,
    campaigns: null,
    selectedCampaign: null,
    ambassadors: null,
    selectedCampaignAmbassadors: null,
    selectedCampaignUpdates: null,
    selectedCampaignUpdate: null,
    latentOrganizationCodes: null,
    classyCampaigns: null
}

export const getFundraisingOrganizationData: any = createAsyncThunk(
    'fundraising/getFundraisingOrganizationData',
    async (organizationId: string, { dispatch, getState }) => {
        try {
            dispatch(setLoading({ loading: true, message: 'Retrieving organization information...' }))

            const { authAccount }: any = getState()

            const apiName = 'RESTfundraising';
            const path = '/fundraising/organization';

            const response = await API.get(apiName, path, { "queryStringParameters": { "jwt": authAccount.cognitoData.session.idToken, "organizationID": authAccount.selectedStaffProfile.organizationID } });

            console.log('get fundraising organization response: ', response)

            dispatch(setLoading({ loading: false, message: 'Loading.' }))

            return response
        } catch (err) {
            console.error(err)
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

export const getFundraisingConnectedAccountLink: any = createAsyncThunk(
    'fundraising/getFundraisingConnectedAccountLink',
    async (organizationId: string, { dispatch, getState }) => {
        try {
            dispatch(setLoading({ loading: true, message: 'Redirecting you to your payout page...' }))

            const { authAccount }: any = getState()

            const apiName = 'RESTfundraising';
            const path = '/fundraising/stripeConnectedAccountLink';

            const response = await API.post(apiName, path, { body: { "jwt": authAccount.cognitoData.session.idToken, "organizationID": authAccount.selectedStaffProfile.organizationID } });

            console.log(response)

            dispatch(setLoading({ loading: false, message: 'Redirecting you to your payout page...' }))

            return response
        } catch (err) {
            console.error(err)
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

export const completeConnectedAccountSetup: any = createAsyncThunk(
    'fundraising/completeConnectedAccountSetup',
    async (organizationId: string, { dispatch, getState }) => {
        try {
            dispatch(setLoading({ loading: true, message: 'Completing payout account setup...' }))

            const { authAccount }: any = getState()

            const apiName = 'RESTfundraising';
            const path = '/fundraising/stripeConnectedAccountLink';

            const response = await API.patch(apiName, path, { body: { "jwt": authAccount.cognitoData.session.idToken, "organizationID": authAccount.selectedStaffProfile.organizationID } });

            console.log(response)

            dispatch(setLoading({ loading: false, message: 'Completing payout account setup...' }))

            return response
        } catch (err) {
            console.error(err)
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

export const getFundraisingCampaigns: any = createAsyncThunk(
    'fundraising/getFundraisingCampaigns',
    async (organizationId: string, { dispatch, getState }) => {
        try {
            dispatch(setLoading({ loading: true, message: 'Getting campaigns...' }))

            const { authAccount }: any = getState()

            if (authAccount && authAccount.selectedStaffProfile) {
                const { data }: any = await API.graphql({
                    query: listFundraisingCampaignsByOrganization,
                    variables: {
                        organizationID: authAccount.selectedStaffProfile.organizationID
                    }
                })

                console.log(data)

                if (data.listFundraisingCampaignsByOrganization && data.listFundraisingCampaignsByOrganization.items) {
                    dispatch(setLoading({ loading: false, message: 'Loading.' }))
                    return {
                        fundraisingCampaigns: data.listFundraisingCampaignsByOrganization.items
                    }
                }
            } else {
                dispatch(setLoading({ loading: false, message: 'Loading.' }))
                console.log(authAccount)
            }

            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        } catch (err) {
            console.log(err)
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

export const setSelectedFundraisingCampaign: any = createAsyncThunk(
    'fundraising/setSelectedFundraisingCampaign',
    async (props: { id: string }, { dispatch, getState }) => {
        try {
            dispatch(setLoading({ loading: true, message: 'Getting campaigns...' }))

            const { authAccount }: any = getState()

            if (authAccount && authAccount.selectedStaffProfile) {
                const { data }: any = await API.graphql({
                    query: getFundraisingCampaign,
                    variables: {
                        id: props.id
                    }
                })

                console.log(data)

                if (data.getFundraisingCampaign) {
                    dispatch(setLoading({ loading: false, message: 'Loading.' }))
                    dispatch(push(`/admin/fundraising/campaigns/manage?id=${data.getFundraisingCampaign.id}`))
                    return {
                        fundraisingCampaign: data.getFundraisingCampaign
                    }
                }
            } else {
                dispatch(setLoading({ loading: false, message: 'Loading.' }))
                console.log(authAccount)
            }

            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        } catch (err) {
            console.log(err)
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

export const getLatentOrganizationCodes: any = createAsyncThunk(
    'fundraising/getLatentOrganizationCodes',
    async (props: any, { dispatch, getState }) => {
        try {
            dispatch(setLoading({ loading: true, message: 'Getting campaigns...' }))

            const { authAccount }: any = getState()

            if (authAccount && authAccount.selectedStaffProfile) {
                const { data }: any = await API.graphql({
                    query: listOrganizationCodesByOrganizationID,
                    variables: {
                        organizationID: authAccount.selectedStaffProfile.organizationID
                    }
                })

                console.log(data)

                if (data.listOrganizationCodesByOrganizationID && data.listOrganizationCodesByOrganizationID.items) {
                    dispatch(setLoading({ loading: false, message: 'Loading.' }))
                    return {
                        latentOrganizationCodes: data.listOrganizationCodesByOrganizationID.items
                    }
                }
            } else {
                dispatch(setLoading({ loading: false, message: 'Loading.' }))
                console.log(authAccount)
            }

            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        } catch (err) {
            console.log(err)
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

export const createLatentInvitationCode: any = createAsyncThunk(
    'fundraising/createLatentOrganizationCode',
    async (props: {
        code: string,
        active: boolean,
        emailRestricted: boolean,
        emailHostRestriction: string | null,
        fundraisingCampaignID: string | null,
        presetFundraisingCampaignAmbassadorStatus: FundraisingCampaignAmbassadorStatus | null
    }, { dispatch, getState }) => {
        try {
            dispatch(setLoading({ loading: true, message: 'Adding invitation code...' }))

            const { authAccount }: any = getState()

            if (authAccount && authAccount.selectedStaffProfile) {

                const checkIfCodeAlreadyExists: any = await API.graphql({
                    query: listOrganizationsByCode,
                    variables: {
                        code: props.code
                    }
                })

                if (checkIfCodeAlreadyExists.data && checkIfCodeAlreadyExists.data.listOrganizationsByCode && checkIfCodeAlreadyExists.data.listOrganizationsByCode.items.length == 0) {
                    const { data }: any = await API.graphql({
                        query: createLatentOrganizationCode,
                        variables: {
                            input: {
                                code: props.code,
                                organizationID: authAccount.selectedStaffProfile.organizationID,
                                active: props.active,
                                emailRestricted: props.emailRestricted,
                                emailHostRestriction: props.emailHostRestriction,
                                fundraisingCampaignID: props.fundraisingCampaignID,
                                presetFundraisingCampaignAmbassadorStatus: props.presetFundraisingCampaignAmbassadorStatus ? props.presetFundraisingCampaignAmbassadorStatus : FundraisingCampaignAmbassadorStatus.Connected
                            }
                        }
                    })

                    console.log(data)

                    if (data.createLatentOrganizationCode) {
                        dispatch(setLoading({ loading: false, message: 'Loading.' }))
                        return {
                            latentOrganizationCode: data.createLatentOrganizationCode
                        }
                    }
                } else {
                    toast.error('An invitation code with that code word already exists. Choose a different code word', { containerId: 'main' })
                }
            } else {
                dispatch(setLoading({ loading: false, message: 'Loading.' }))
                console.log(authAccount)
            }

            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        } catch (err) {
            console.log(err)
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

interface CreateFundraisingCampaignProps {
    id: string | null
    type: string
    name: string
    code: string
    introVideoFile: any
    staffLead: StaffProfile | null
    textMessageTemplate: string
    initialTextReminderTemplate: string | null,
    phoneCallScriptTemplate: string | null
    followUpMessageTemplate: string | null,
    followUpYesMessageTemplate: string | null,
    followUpNoMessageTemplate: string | null,
    followUpReminderTemplate: string | null,
    campaignEndDate: string | null,
    campaignGoal: number | null,
    perAmbassadorGoal: number | null,
    perContactDonationGoal: number | null,
    givingCircleBaseAmount: number,
    givingCircleIncrements: number[],
    givingCircleAllowCustomAmounts: boolean,
    ctaURL: string | null,
    crowbarVideoURL: string | null,
    ambassadorSpecificCtaUrlEnabled: boolean,
    ambassadorCanUploadCtaUrl: boolean,
    ambassadorSpecificVideoUrlEnabled: boolean,
    ambassadorCanUploadVideoUrl: boolean,
    recruitmentForCampaignID: string | null,
    useClassyCtaURL: boolean | null,
    classyDonationPageHostURL: string | null,
    classyCampaignID: string | null,
    classyAutogenerateAmbassadorTeams: boolean | null,
    personalFundraisingCampaignID: string | null,
    useClassyFundraiserPage: boolean
}

export const createBackendFundraisingCampaign: any = createAsyncThunk(
    'fundraising/createBackendFundraisingCampaign',
    async (props: CreateFundraisingCampaignProps, { dispatch, getState }) => {
        try {
            dispatch(setLoading({ loading: true, message: props.id ? 'Updating campaign...' : 'Creating campaign...' }))

            const { authAccount }: any = getState()

            console.log(authAccount)

            if (authAccount && authAccount.selectedStaffProfile) {

                if (props.id) {
                    const { data }: any = await API.graphql({
                        query: updateFundraisingCampaign,
                        variables: {
                            input: {
                                id: props.id,
                                type: props.type,
                                organizationID: authAccount.selectedStaffProfile.organizationID,
                                staffLeadID: props.staffLead ? props.staffLead.id : '00000000-0000-0000-0000-000000000000',
                                campaignName: props.name,
                                textMessageTemplate: props.textMessageTemplate,
                                initialMessageReminderTemplate: props.initialTextReminderTemplate,
                                phoneCallScriptTemplate: props.phoneCallScriptTemplate,
                                followUpMessageTemplate: props.followUpMessageTemplate,
                                followUpYesMessageTemplate: props.followUpYesMessageTemplate,
                                followUpNoMessageTemplate: props.followUpNoMessageTemplate,
                                followUpReminderMessageTemplate: props.followUpReminderTemplate,
                                showVideo: props.introVideoFile == null || props.introVideoFile == '' ? false : true,
                                showText: true,
                                showCall: props.phoneCallScriptTemplate == null || props.phoneCallScriptTemplate == '' ? false : true,
                                showFollowUp: ((props.followUpYesMessageTemplate == null || props.followUpYesMessageTemplate == '') && (props.followUpNoMessageTemplate == null || props.followUpNoMessageTemplate == '')) ? false : true,
                                active: false,
                                campaignEndDate: props.campaignEndDate,
                                campaignGoal: props.campaignGoal,
                                perAmbassadorGoal: props.perAmbassadorGoal,
                                perContactDonationGoal: props.perContactDonationGoal,
                                givingCircleBaseAmount: props.givingCircleBaseAmount,
                                givingCircleIncrements: props.givingCircleIncrements,
                                givingCircleAllowCustomAmounts: props.givingCircleAllowCustomAmounts,
                                ctaURL: props.ctaURL,
                                crowbarVideoURL: props.crowbarVideoURL,
                                ambassadorSpecificCtaUrlEnabled: props.ambassadorSpecificCtaUrlEnabled,
                                ambassadorCanUploadCtaUrl: props.ambassadorCanUploadCtaUrl,
                                ambassadorSpecificVideoUrlEnabled: props.ambassadorSpecificVideoUrlEnabled,
                                ambassadorCanUploadVideoUrl: props.ambassadorCanUploadVideoUrl,
                                recruitmentForCampaignID: props.recruitmentForCampaignID,
                                useClassyCtaURL: props.useClassyCtaURL,
                                classyDonationPageHostURL: props.classyDonationPageHostURL,
                                classyCampaignID: props.classyCampaignID,
                                classyAutogenerateAmbassadorTeams: props.classyAutogenerateAmbassadorTeams,
                                personalFundraisingCampaignID: props.personalFundraisingCampaignID,
                                useClassyFundraiserPage: props.useClassyFundraiserPage
                            }
                        }
                    })

                    console.log(data)

                    if (data && data.updateFundraisingCampaign && props.introVideoFile) {

                        console.log(props.introVideoFile)

                        const uploadFileResponse = await Storage.put(`campaigns/${data.updateFundraisingCampaign.id}/introVideo`, props.introVideoFile, {
                            contentType: props.introVideoFile.type
                        })

                        console.log(uploadFileResponse)

                        const updateFundraisingCampaignResponse: any = await API.graphql({
                            query: updateFundraisingCampaign,
                            variables: {
                                input: {
                                    id: data.updateFundraisingCampaign.id,
                                    introVideoURI: `campaigns/${data.updateFundraisingCampaign.id}/introVideo`
                                }
                            }
                        })

                        console.log(updateFundraisingCampaignResponse)
                    }

                    if (data.updateFundraisingCampaign) {
                        dispatch(setLoading({ loading: false, message: 'Loading.' }))
                        return {
                            fundraisingCampaign: data.updateFundraisingCampaign
                        }
                    } else {
                        dispatch(setLoading({ loading: false, message: 'Loading.' }))
                    }
                } else {
                    const { data }: any = await API.graphql({
                        query: createFundraisingCampaign,
                        variables: {
                            input: {
                                type: props.type,
                                organizationID: authAccount.selectedStaffProfile.organizationID,
                                staffLeadID: props.staffLead ? props.staffLead.id : '00000000-0000-0000-0000-000000000000',
                                campaignName: props.name,
                                textMessageTemplate: props.textMessageTemplate,
                                phoneCallScriptTemplate: props.phoneCallScriptTemplate,
                                followUpMessageTemplate: props.followUpMessageTemplate,
                                followUpYesMessageTemplate: props.followUpYesMessageTemplate,
                                followUpNoMessageTemplate: props.followUpNoMessageTemplate,
                                showVideo: props.introVideoFile == null || props.introVideoFile == '' ? false : true,
                                showText: true,
                                showCall: props.phoneCallScriptTemplate == null || props.phoneCallScriptTemplate == '' ? false : true,
                                showFollowUp: ((props.followUpYesMessageTemplate == null || props.followUpYesMessageTemplate == '') && (props.followUpNoMessageTemplate == null || props.followUpNoMessageTemplate == '')) ? false : true,
                                active: false,
                                campaignEndDate: props.campaignEndDate,
                                campaignGoal: props.campaignGoal,
                                perAmbassadorGoal: props.perAmbassadorGoal,
                                perContactDonationGoal: props.perContactDonationGoal,
                                ctaURL: props.ctaURL,
                                crowbarVideoURL: props.crowbarVideoURL,
                                ambassadorSpecificCtaUrlEnabled: props.ambassadorSpecificCtaUrlEnabled,
                                ambassadorCanUploadCtaUrl: props.ambassadorCanUploadCtaUrl,
                                ambassadorSpecificVideoUrlEnabled: props.ambassadorSpecificVideoUrlEnabled,
                                ambassadorCanUploadVideoUrl: props.ambassadorCanUploadVideoUrl,
                                recruitmentForCampaignID: props.recruitmentForCampaignID,
                                useClassyCtaURL: props.useClassyCtaURL,
                                classyDonationPageHostURL: props.classyDonationPageHostURL,
                                classyCampaignID: props.classyCampaignID,
                                classyAutogenerateAmbassadorTeams: props.classyAutogenerateAmbassadorTeams
                            }
                        }
                    })

                    console.log(data)

                    if (data && data.createFundraisingCampaign && props.introVideoFile) {
                        const uploadFileResponse = await Storage.put(`campaigns/${data.createFundraisingCampaign.id}/introVideo`, props.introVideoFile, {
                            contentType: 'video/mp4'
                        })

                        console.log(uploadFileResponse)

                        const updateFundraisingCampaignResponse: any = await API.graphql({
                            query: updateFundraisingCampaign,
                            variables: {
                                input: {
                                    id: data.createFundraisingCampaign.id,
                                    introVideoURI: `campaigns/${data.createFundraisingCampaign.id}/introVideo`
                                }
                            }
                        })

                        console.log(updateFundraisingCampaignResponse)
                    }

                    if (data.createFundraisingCampaign) {
                        dispatch(setLoading({ loading: false, message: 'Loading.' }))
                        return {
                            fundraisingCampaign: data.createFundraisingCampaign
                        }
                    } else {
                        dispatch(setLoading({ loading: false, message: 'Loading.' }))
                    }
                }
            } else {
                dispatch(setLoading({ loading: false, message: 'Loading.' }))
            }
        } catch (err) {
            console.log(err)
            dispatch(setLoading({ loading: false, message: 'Loading.' }))
        }
    }
)

interface UpdatePublicFundraisingCampaignDetails {
    publicVideoID?: string,
    publicVideoProvider?: string,
    publicCampaignAbout?: string,
    progressChartType?: string,
    publicCampaignIntro?: string | null,
    primaryColor?: string | null,
    buttonBackgroundColor?: string | null,
    buttonTextColor?: string | null,
    secondaryColor?: string | null,
    headerFont?: string | null,
    bodyFont?: string | null
}

export const updatePublicFundraisingCampaignDetails: any = createAsyncThunk(
    'fundraising/updatePublicFundraisingCampaignDetails',
    async (props: UpdatePublicFundraisingCampaignDetails, { dispatch, getState }) => {

        dispatch(setLoading({ loading: true, message: 'Updating campaign information' }))

        const { fundraising }: any = getState()

        if (fundraising && fundraising.selectedCampaign) {
            const updateFundraisingCampaignResponse: any = await API.graphql({
                query: updateFundraisingCampaign,
                variables: {
                    input: {
                        id: fundraising.selectedCampaign.id,
                        publicVideoID: props.publicVideoID ? props.publicVideoID : null,
                        publicVideoProvider: props.publicVideoProvider ? props.publicVideoProvider : null,
                        publicCampaignAbout: props.publicCampaignAbout ? props.publicCampaignAbout : null,
                        progressChartType: props.progressChartType ? props.progressChartType : null,
                        publicCampaignIntro: props.publicCampaignIntro ? props.publicCampaignIntro : null,
                        primaryColor: props.primaryColor ? props.primaryColor : null,
                        buttonBackgroundColor: props.buttonBackgroundColor ? props.buttonBackgroundColor : null,
                        buttonTextColor: props.buttonTextColor ? props.buttonTextColor : null,
                        secondaryColor: props.secondaryColor ? props.secondaryColor : null,
                        headerFont: props.headerFont ? props.headerFont : null,
                        bodyFont: props.bodyFont ? props.bodyFont : null
                    }
                }
            })

            if (updateFundraisingCampaignResponse.data && updateFundraisingCampaignResponse.data.updateFundraisingCampaign) {
                dispatch(setLoading({ loading: false, message: 'Updating campaign information' }))

                return {
                    fundraisingCampaign: updateFundraisingCampaignResponse.data.updateFundraisingCampaign
                }
            } else {
                dispatch(setLoading({ loading: false, message: 'Updating campaign information' }))
            }
        } else {
            dispatch(setLoading({ loading: false, message: 'Updating campaign information' }))
        }
    }
)


export const inviteFundraisingAmbassador: any = createAsyncThunk(
    'fundraising/inviteFundraisingAmbassador',
    async (props: { email: string, phoneNumber: string | null, firstName: string | null, lastName: string | null }, { dispatch, getState }) => {
        try {

            const { authAccount, organization }: any = getState()

            const createFundraisingProfileResponse: any = await API.graphql({
                query: createFundraisingProfile,
                variables: {
                    input: {
                        organizationID: authAccount.selectedStaffProfile.organization.id,
                        email: props.email,
                        firstName: props.firstName,
                        lastName: props.lastName
                    }
                }
            })

            console.log('create fundraising profile response: ', createFundraisingProfileResponse.data)

            const { data }: any = await API.graphql({
                query: createFundraisingInvitation,
                variables: {
                    input: {
                        code: makeid(6),
                        email: props.email,
                        firstName: props.firstName,
                        lastName: props.lastName,
                        organizationID: authAccount.selectedStaffProfile.organization.id,
                        fundraisingProfileID: createFundraisingProfileResponse.data.createFundraisingProfile.id,
                        used: false,
                        invitationSent: true,
                        invitationSentTimestamp: new Date()
                    }
                }
            })

            console.log('new invitation data: ', data)

            const invitation = data.createStaffInvitation

            const sendInvitationEmail = await API.graphql({
                query: sendFundraisingInvitationEmail,
                variables: {
                    input: {
                        organizationName: authAccount.selectedStaffProfile.organization.name,
                        fromEmail: authAccount.cognitoData.email,
                        fromName: authAccount.selectedStaffProfile.organization.name,
                        toName: props.firstName,
                        toEmail: props.email,
                        toPhoneNumber: props.phoneNumber ? props.phoneNumber : null,
                        code: data.createFundraisingInvitation.code,
                        organizationId: authAccount.selectedStaffProfile.organization.id
                    }
                }
            })

            console.log('new invitation sent: ', sendInvitationEmail)

            toast.success('Invitation sent.', {
                position: "bottom-right",
                autoClose: 4000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                progress: undefined,
                containerId: 'main'
            });

            dispatch(addFundraisingAmbassador(createFundraisingProfileResponse.data.createFundraisingProfile))

            return {
                invitation,
                fundraisingProfile: createFundraisingProfileResponse.data.createFundraisingProfile
            }
        } catch (err) {
            console.error(err)
        }
    }
)

export const removeFundraisingAmbassador: any = createAsyncThunk(
    'fundraising/removeFundraisingAmbassador',
    async (props: { fundraisingProfile: FundraisingProfile }, { dispatch, getState }) => {

        dispatch(setLoading({ loading: true, message: 'Removing fundraising profile...' }))

        const { authAccount }: any = getState()

        if (props.fundraisingProfile.organizationID && authAccount.selectedStaffProfile && props.fundraisingProfile.organizationID == authAccount.selectedStaffProfile.organizationID) {
            const { data }: any = await API.graphql({
                query: deleteFundraisingProfile,
                variables: {
                    input: {
                        id: props.fundraisingProfile.id
                    }
                }
            })

            console.log(data)

            dispatch(getFundraisingAmbassadors())
        } else {
            toast.error("You don't have permission to do that.", { containerId: 'main' })
        }

        dispatch(setLoading({ loading: false, message: '' }))

    }
)

export const getFundraisingAmbassadors: any = createAsyncThunk(
    'fundraising/getFundraisingAmbassadors',
    async (props: any, { dispatch, getState }) => {
        try {

            const { authAccount, organization }: any = getState()

            const listFundraisingProfilesByOrganizationResponse: any = await API.graphql({
                query: listFundraisingProfilesByOrganization,
                variables: {
                    organizationID: authAccount.selectedStaffProfile.organization.id,
                    limit: 500
                }
            })

            console.log(listFundraisingProfilesByOrganizationResponse)

            if (listFundraisingProfilesByOrganizationResponse.data.listFundraisingProfilesByOrganization) {
                return {
                    fundraisingProfiles: listFundraisingProfilesByOrganizationResponse.data.listFundraisingProfilesByOrganization.items
                }
            }
        } catch (err) {
            console.error(err)
        }
    }
)

export const getFundraisingCampaignAmbassadors: any = createAsyncThunk(
    'fundraising/getFundraisingCampaignAmbassadors',
    async (props: { campaignId?: string }, { dispatch, getState }) => {
        try {

            const { fundraising }: any = getState()

            if (props && props.campaignId) {
                const listFundraisingCampaignAmbassadorsByCampaignResponse: any = await API.graphql({
                    query: listFundraisingCampaignAmbassadorsByCampaign,
                    variables: {
                        fundraisingCampaignID: props.campaignId
                    }
                })

                console.log(listFundraisingCampaignAmbassadorsByCampaignResponse)

                if (listFundraisingCampaignAmbassadorsByCampaignResponse.data.listFundraisingCampaignAmbassadorsByCampaign) {
                    return {
                        campaignAmbassadors: listFundraisingCampaignAmbassadorsByCampaignResponse.data.listFundraisingCampaignAmbassadorsByCampaign.items
                    }
                }
            } else if (fundraising.selectedCampaign) {
                const listFundraisingCampaignAmbassadorsByCampaignResponse: any = await API.graphql({
                    query: listFundraisingCampaignAmbassadorsByCampaign,
                    variables: {
                        fundraisingCampaignID: fundraising.selectedCampaign.id
                    }
                })

                console.log(listFundraisingCampaignAmbassadorsByCampaignResponse)

                if (listFundraisingCampaignAmbassadorsByCampaignResponse.data.listFundraisingCampaignAmbassadorsByCampaign) {
                    return {
                        campaignAmbassadors: listFundraisingCampaignAmbassadorsByCampaignResponse.data.listFundraisingCampaignAmbassadorsByCampaign.items
                    }
                }
            }


        } catch (err) {
            console.error(err)
        }
    }
)

export const addFundraisingCampaignAmbassador: any = createAsyncThunk(
    'fundraising/addFundraisingCampaignAmbassador',
    async (props: { fundraisingAmbassador: FundraisingProfile }, { dispatch, getState }) => {
        try {

            const { fundraising }: any = getState()

            const checkIfAlreadyCreated: any = await API.graphql({
                query: listFundraisingCampaignAmbassadorsByFundraisingProfile,
                variables: {
                    fundraisingProfileID: props.fundraisingAmbassador.id,
                    filter: {
                        fundraisingCampaignID: {
                            eq: fundraising.selectedCampaign.id
                        }
                    }
                }
            })

            console.log(checkIfAlreadyCreated.data)

            if (checkIfAlreadyCreated && checkIfAlreadyCreated.data && checkIfAlreadyCreated.data.listFundraisingCampaignAmbassadorsByFundraisingProfile && checkIfAlreadyCreated.data.listFundraisingCampaignAmbassadorsByFundraisingProfile.items.length == 0) {
                const createFundraisingCampaignAmbassadorResponse: any = await API.graphql({
                    query: createFundraisingCampaignAmbassador,
                    variables: {
                        input: {
                            fundraisingProfileID: props.fundraisingAmbassador.id,
                            fundraisingCampaignID: fundraising.selectedCampaign.id,
                            status: FundraisingCampaignAmbassadorStatus.Waiting
                        }
                    }
                })

                if (createFundraisingCampaignAmbassadorResponse.data.createFundraisingCampaignAmbassador) {
                    return {
                        campaignAmbassador: createFundraisingCampaignAmbassadorResponse.data.createFundraisingCampaignAmbassador
                    }
                }
            } else {
                toast.error('This user is already added to this campaign.', { containerId: 'main' })
            }
        } catch (err) {
            console.error(err)
        }
    }
)

export const getFundraisingCampaignUpdates: any = createAsyncThunk(
    'fundraising/getFundraisingCampaignUpdates',
    async (props: { campaignId?: string }, { dispatch, getState }) => {
        try {

            const { fundraising }: any = getState()

            if (props.campaignId) {
                const listFundraisingCampaignUpdatesByFundraisingCampaignResponse: any = await API.graphql({
                    query: listFundraisingCampaignUpdatesByFundraisingCampaign,
                    variables: {
                        fundraisingCampaignID: props.campaignId
                    }
                })

                console.log(listFundraisingCampaignUpdatesByFundraisingCampaignResponse)

                if (listFundraisingCampaignUpdatesByFundraisingCampaignResponse.data.listFundraisingCampaignUpdatesByFundraisingCampaign) {
                    return {
                        campaignUpdates: listFundraisingCampaignUpdatesByFundraisingCampaignResponse.data.listFundraisingCampaignUpdatesByFundraisingCampaign.items
                    }
                }
            } else if (fundraising.selectedCampaign) {
                const listFundraisingCampaignUpdatesByFundraisingCampaignResponse: any = await API.graphql({
                    query: listFundraisingCampaignUpdatesByFundraisingCampaign,
                    variables: {
                        fundraisingCampaignID: fundraising.selectedCampaign.id
                    }
                })

                console.log(listFundraisingCampaignUpdatesByFundraisingCampaignResponse)

                if (listFundraisingCampaignUpdatesByFundraisingCampaignResponse.data.listFundraisingCampaignUpdatesByFundraisingCampaign) {
                    return {
                        campaignUpdates: listFundraisingCampaignUpdatesByFundraisingCampaignResponse.data.listFundraisingCampaignUpdatesByFundraisingCampaign.items
                    }
                }
            }


        } catch (err) {
            console.error(err)
        }
    }
)

export const manageFundraisingCampaignUpdate: any = createAsyncThunk(
    'fundraising/manageCampaignUpdate',
    async (props: { id: string | null, message: string, public: boolean, live: boolean, url: string | null }, { dispatch, getState }) => {
        try {

            const { fundraising, authAccount }: any = getState()

            if (props.id) {
                const updateFundraisingCampaignUpdateResponse: any = await API.graphql({
                    query: updateFundraisingCampaignUpdate,
                    variables: {
                        input: {
                            id: props.id,
                            message: props.message,
                            updateURL: props.url,
                            public: props.public,
                            live: props.live,
                            createdByStaffProfileID: authAccount.selectedStaffProfile.id,
                            fundraisingCampaignID: fundraising.selectedCampaign.id
                        }
                    }
                })

                if (updateFundraisingCampaignUpdateResponse.data.updateFundraisingCampaignUpdate) {
                    return {
                        campaignUpdate: updateFundraisingCampaignUpdateResponse.data.updateFundraisingCampaignUpdate
                    }
                }
            } else {
                const createFundraisingCampaignUpdateResponse: any = await API.graphql({
                    query: createFundraisingCampaignUpdate,
                    variables: {
                        input: {
                            message: props.message,
                            updateURL: props.url,
                            public: props.public,
                            live: props.live,
                            createdByStaffProfileID: authAccount.selectedStaffProfile.id,
                            fundraisingCampaignID: fundraising.selectedCampaign.id
                        }
                    }
                })

                if (createFundraisingCampaignUpdateResponse.data.createFundraisingCampaignUpdate) {
                    return {
                        campaignUpdate: createFundraisingCampaignUpdateResponse.data.createFundraisingCampaignUpdate
                    }
                }
            }
        } catch (err) {
            console.error(err)
        }
    }
)

export const bulkAcceptCampaignAmbassadors: any = createAsyncThunk(
    'fundraising/bulkAcceptCampaignAmbassadors',
    async (props: { ids: string[] }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({ loading: true, message: 'Updating...' }))

            const { fundraising, authAccount }: any = getState()

            var successfulIterations = 0;

            if (props.ids.length > 0) {
                await asyncForEach(props.ids, async (id) => {
                    const { data }: any = await API.graphql({
                        query: updateFundraisingCampaignAmbassador,
                        variables: {
                            input: {
                                id,
                                status: FundraisingCampaignAmbassadorStatus.Accepted
                            }
                        }
                    })

                    if (data.updateFundraisingCampaignAmbassador) successfulIterations += 1
                })

                await dispatch(getFundraisingCampaignAmbassadors({ campaignId: undefined }))

                if (successfulIterations == props.ids.length) {
                    dispatch(setLoading({ loading: false, message: '' }))
                    return {
                        status: 'success',
                        message: 'Successfully updated Campaign Ambassador statuses.'
                    }
                }
            }

            dispatch(setLoading({ loading: false, message: '' }))
        } catch (err) {
            dispatch(setLoading({ loading: false, message: '' }))
            console.error(err)
        }
    }
)

export const bulkDeclineCampaignAmbassadors: any = createAsyncThunk(
    'fundraising/bulkDeclineCampaignAmbassadors',
    async (props: { ids: string[] }, { dispatch, getState }) => {
        try {

            console.log(props.ids)

            dispatch(setLoading({ loading: true, message: 'Updating...' }))

            const { fundraising, authAccount }: any = getState()

            var successfulIterations = 0;

            if (props.ids.length > 0) {
                await asyncForEach(props.ids, async (id) => {
                    const { data }: any = await API.graphql({
                        query: updateFundraisingCampaignAmbassador,
                        variables: {
                            input: {
                                id,
                                status: FundraisingCampaignAmbassadorStatus.Declined
                            }
                        }
                    })

                    console.log(data)

                    if (data.updateFundraisingCampaignAmbassador) successfulIterations += 1
                })

                await dispatch(getFundraisingCampaignAmbassadors({ campaignId: undefined }))

                if (successfulIterations == props.ids.length) {
                    dispatch(setLoading({ loading: false, message: '' }))
                    return {
                        status: 'success',
                        message: 'Successfully updated Campaign Ambassador statuses.'
                    }
                }
            }

            dispatch(setLoading({ loading: false, message: '' }))
        } catch (err) {
            dispatch(setLoading({ loading: false, message: '' }))
            console.error(err)
        }
    }
)

export const bulkWaitingCampaignAmbassadors: any = createAsyncThunk(
    'fundraising/bulkWaitingCampaignAmbassadors',
    async (props: { ids: string[] }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({ loading: true, message: 'Updating...' }))

            const { fundraising, authAccount }: any = getState()

            var successfulIterations = 0;

            if (props.ids.length > 0) {
                await asyncForEach(props.ids, async (id) => {
                    const { data }: any = await API.graphql({
                        query: updateFundraisingCampaignAmbassador,
                        variables: {
                            input: {
                                id,
                                status: FundraisingCampaignAmbassadorStatus.Waiting
                            }
                        }
                    })

                    if (data.updateFundraisingCampaignAmbassador) successfulIterations += 1
                })

                await dispatch(getFundraisingCampaignAmbassadors({ campaignId: undefined }))

                if (successfulIterations == props.ids.length) {
                    dispatch(setLoading({ loading: false, message: '' }))
                    return {
                        status: 'success',
                        message: 'Successfully updated Campaign Ambassador statuses.'
                    }
                }
            }

            dispatch(setLoading({ loading: false, message: '' }))
        } catch (err) {
            dispatch(setLoading({ loading: false, message: '' }))
            console.error(err)
        }
    }
)

export const bulkRemoveCampaignAmbassadors: any = createAsyncThunk(
    'fundraising/bulkRemoveCampaignAmbassadors',
    async (props: { ids: string[] }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({ loading: true, message: 'Updating...' }))

            const { fundraising, authAccount }: any = getState()

            var successfulIterations = 0;

            if (props.ids.length > 0) {
                await asyncForEach(props.ids, async (id) => {
                    const { data }: any = await API.graphql({
                        query: deleteFundraisingCampaignAmbassador,
                        variables: {
                            input: {
                                id
                            }
                        }
                    })

                    if (data.updateFundraisingCampaignAmbassador) successfulIterations += 1
                })

                await dispatch(getFundraisingCampaignAmbassadors({ campaignId: undefined }))

                if (successfulIterations == props.ids.length) {
                    dispatch(setLoading({ loading: false, message: '' }))
                    return {
                        status: 'success',
                        message: 'Successfully deleted Campaign Ambassador(s).'
                    }
                }
            }

            dispatch(setLoading({ loading: false, message: '' }))
        } catch (err) {
            dispatch(setLoading({ loading: false, message: '' }))
            console.error(err)
        }
    }
)

export const getClassyCampaigns: any = createAsyncThunk(
    'fundraising/getClassyCampaigns',
    async (props: { ids: string[] }, { dispatch, getState }) => {
        try {

            dispatch(setLoading({
                loading: true,
                message: 'Loading Classy integration settings...'
            }))

            const { authAccount }: any = getState()

            const user = await Auth.currentAuthenticatedUser({ bypassCache: false })

            if (authAccount.selectedStaffProfile) {
                const response = await API.get('RESTfundraising', '/latent/integrations/classy/campaigns', {
                    queryStringParameters: {
                        jwt: user.signInUserSession.idToken.jwtToken,
                        organizationID: authAccount.selectedStaffProfile.organizationID
                    }
                })

                console.log(response)

                if (response && response.classyCampaigns) {
                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))
                    return {
                        classyCampaigns: response.classyCampaigns
                    }
                } else {
                    dispatch(setLoading({
                        loading: false,
                        message: 'Loading.'
                    }))
                    return {
                        activeConnections: []
                    }
                }
            }

            dispatch(setLoading({
                loading: false,
                message: 'Loading.'
            }))

            dispatch(setLoading({ loading: false, message: '' }))
        } catch (err) {
            dispatch(setLoading({ loading: false, message: '' }))
            console.error(err)
        }
    }
)

export const fundraisingSlice = createSlice({
    name: 'fundraising',
    initialState,
    reducers: {
        clearLocalFundraisingData: (state) => {
            state.connectedAccountLink = null
        },
        clearSelectedCampaign: (state, { payload }) => {
            state.selectedCampaign = null
        },
        addFundraisingAmbassador: (state, { payload }) => {
            if (state.ambassadors) {
                state.ambassadors.push(payload)
            } else {
                state.ambassadors = [payload]
            }
        },
        setSelectedCampaignUpdate: (state, { payload }) => {
            console.log(payload)
            if (payload && payload.campaignUpdate) {
                state.selectedCampaignUpdate = payload.campaignUpdate
            }
        },
        clearSelectedCampaignUpdate: (state, { payload }) => {
            state.selectedCampaignUpdate = null
        }
    },
    extraReducers: (builder) => {
        builder.addCase(getFundraisingConnectedAccountLink.fulfilled, (state, { payload }) => {
            if (payload.url) state.connectedAccountLink = payload.url
            else state.connectedAccountLink = '#'
        })
        builder.addCase(getFundraisingOrganizationData.fulfilled, (state, { payload }) => {
            if (payload && payload.code == 'NotFound') {
                state.fundraisingOrganization = {}
                state.connectedAccountLink = '#'
            }
            else state.fundraisingOrganization = payload
        })
        builder.addCase(getFundraisingCampaigns.fulfilled, (state, { payload }) => {
            if (payload && payload.fundraisingCampaigns) {
                state.campaigns = payload.fundraisingCampaigns
            }
        })
        builder.addCase(setSelectedFundraisingCampaign.fulfilled, (state, { payload }) => {
            if (payload && payload.fundraisingCampaign) {
                state.selectedCampaign = payload.fundraisingCampaign
            }
        })
        builder.addCase(getFundraisingAmbassadors.fulfilled, (state, { payload }) => {
            if (payload && payload.fundraisingProfiles) state.ambassadors = payload.fundraisingProfiles
        })
        builder.addCase(getFundraisingCampaignAmbassadors.fulfilled, (state, { payload }) => {
            if (payload && payload.campaignAmbassadors) state.selectedCampaignAmbassadors = payload.campaignAmbassadors
        })
        builder.addCase(addFundraisingCampaignAmbassador.fulfilled, (state, { payload }) => {
            if (payload && payload.campaignAmbassador) {
                if (state.selectedCampaignAmbassadors) state.selectedCampaignAmbassadors.push(payload.campaignAmbassador)
                else state.selectedCampaignAmbassadors = [payload.campaignAmbassador]
            }
        })
        builder.addCase(updatePublicFundraisingCampaignDetails.fulfilled, (state, { payload }) => {
            if (payload && payload.fundraisingCampaign) state.selectedCampaign = payload.fundraisingCampaign
        })
        builder.addCase(getFundraisingCampaignUpdates.fulfilled, (state, { payload }) => {
            if (payload && payload.campaignUpdates) state.selectedCampaignUpdates = payload.campaignUpdates
        })
        builder.addCase(getLatentOrganizationCodes.fulfilled, (state, { payload }) => {
            if (payload && payload.latentOrganizationCodes) state.latentOrganizationCodes = payload.latentOrganizationCodes
        })
        builder.addCase(createLatentInvitationCode.fulfilled, (state, { payload }) => {
            if (payload && payload.latentOrganizationCode && state.latentOrganizationCodes) state.latentOrganizationCodes.push(payload.latentOrganizationCode)
            else state.latentOrganizationCodes = [payload.latentOrganizationCode]
        })
        builder.addCase(getClassyCampaigns.fulfilled, (state, { payload }) => {
            console.log(payload)
            if (payload.classyCampaigns) {
                state.classyCampaigns = payload.classyCampaigns
            }
        })
    }
})

export const { clearLocalFundraisingData, addFundraisingAmbassador, clearSelectedCampaign, setSelectedCampaignUpdate, clearSelectedCampaignUpdate } = fundraisingSlice.actions
export default fundraisingSlice.reducer

function makeid(length: number) {
    var result = '';
    var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    var charactersLength = characters.length;
    for (var i = 0; i < length; i++) {
        result += characters.charAt(Math.floor(Math.random() *
            charactersLength));
    }
    return result;
}

async function asyncForEach(array: any[], callback: (item: any, index: number, array: any[]) => void) {
    for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
    }
}